/**
 * Wealthica2 callback orchestration hook
 *
 * Responsibilities:
 * - Detect and guard OAuth callback processing
 * - Coordinate flow to `useWealthicaCallbackHandler` and navigation redirects
 * - Show progress toasts and handle long-running operations and errors
 *
 * Notes:
 * - Pure orchestration: does not persist or fetch data directly
 * - Uses multi-store state; avoids double-processing via refs/timeouts
 */
import React, { useEffect, useRef, useState } from 'react';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { isWealthica2Callback } from '../utils/callback';
import { toast } from 'sonner';
import { startFlowToastBanner, updateFlowToast, updateFlowProgress } from '../utils/flowToast';
import { useWealthicaCallbackHandler } from './useWealthicaCallbackHandler';
import { logger } from '@/services/LoggingService';
import { callbackTimeoutManager } from '../utils/timeout-manager';
import { ConnectionFlowState } from '../utils/flow-state';
import { useInstitutionMeta } from './useInstitutionMeta';
import { useOAuthProtection } from '@/hooks/useOAuthProtection';

export function useWealthicaCallbackOrchestrator(opts?: { enableUI?: boolean }) {
  const [isShowingProcessing, setIsShowingProcessing] = useState(false);

  const multiStore = useMultiWealthica2Store();
  const status = multiStore.activeConnectionId ? (multiStore.getConnection(multiStore.activeConnectionId)?.status || 'idle') : 'idle';
  const error = multiStore.activeConnectionId ? (multiStore.getConnection(multiStore.activeConnectionId)?.error || null) : null;

  const { processCallback } = useWealthicaCallbackHandler();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();

  const processedRef = useRef(false);
  const timeoutRef = useRef<string | null>(null);

  const institution = searchParams.get('institution') || searchParams.get('code');
  const provider = searchParams.get('provider');
  const stateParam = searchParams.get('state');

  const oauthProtection = useOAuthProtection({ provider: 'wealthica2' });

  useEffect(() => {
    if (!isWealthica2Callback()) {
      logger.debug('Wealthica2CallbackHandler', 'Not a Wealthica callback URL', {
        currentUrl: window.location.href,
        timestamp: new Date().toISOString()
      });
      return;
    }

    if (!user) {
      logger.debug('Wealthica2CallbackHandler', 'Auth not ready - waiting for user data', {
        userExists: !!user,
        timestamp: new Date().toISOString(),
        willKeepWaiting: true
      });
      return;
    }

    if (!user.id) {
      logger.warn('Wealthica2CallbackHandler', 'User exists but missing ID', {
        userKeys: Object.keys(user),
        timestamp: new Date().toISOString()
      });
      return;
    }

    ConnectionFlowState.startUserFlow('callback');

    const shouldProcessCallback = !processedRef.current;
    if (!shouldProcessCallback) {
      logger.info('Wealthica2CallbackHandler', 'Skipping callback processing - already processed', {
        status: status,
        alreadyProcessed: processedRef.current
      });
      ConnectionFlowState.endUserFlow();
      return;
    }

    setIsShowingProcessing(true);
    try { startFlowToastBanner('wealthica-flow', 'Processing connection…', 'Redirecting and finalizing', 10); } catch {}

    const operationId = `callback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    logger.info('Wealthica2CallbackHandler', 'FORENSIC TRACE - Callback URL Analysis', {
      operationId,
      currentUrl: window.location.href,
      searchParams: Object.fromEntries(searchParams.entries()),
      extractedInstitution: institution,
      extractedProvider: provider,
      extractedState: stateParam,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });

    if (institution === 'demo' || institution === 'test' || (institution && institution.length < 20)) {
      logger.error('Wealthica2CallbackHandler', 'CRITICAL ERROR: Invalid institution ID in callback URL', {
        operationId,
        institution,
        provider,
        stateParam,
        currentUrl: window.location.href,
        error: 'institution parameter should be a real OAuth ID, not a placeholder value'
      });
      toast.error('Invalid connection parameters. Please restart the connection process.');
      setTimeout(() => { navigate('/investments/connect', { replace: true }); }, 2000);
      return;
    }

    logger.info('Wealthica2CallbackHandler', 'Processing callback', { institution, provider, stateParam });

    processedRef.current = true;

    const timeoutKey = `callback-${institution}-${Date.now()}`;
    timeoutRef.current = timeoutKey;

    logger.info('Wealthica2CallbackHandler', 'CALLBACK PROCESSING START', {
      userId: user.id,
      institution,
      provider,
      stateParam,
      currentUrl: window.location.href,
      timestamp: new Date().toISOString(),
      contextStatus: status,
      processingAttempt: 1
    });

    try {
      const startTime = Date.now();
      const monitorProgress = () => {
        const elapsed = Date.now() - startTime;
        if (elapsed > 15000 && elapsed < 20000) {
          logger.warn('Wealthica2CallbackHandler', 'Callback taking longer than expected', { elapsed, institution, status });
          try { updateFlowProgress('wealthica-flow', 30, 'Still working…', 'This is taking longer than usual'); } catch {}
        }
      };
      const progressTimeoutId = setTimeout(monitorProgress, 15000);

      processCallback({
        userId: user.id,
        navigate,
        onSuccess: () => {
          logger.info('Wealthica2CallbackHandler', 'CALLBACK SUCCESS - Connection established', {
            userId: user.id,
            institution,
            provider,
            timestamp: new Date().toISOString(),
            processingTime: Date.now() - startTime
          });
          clearTimeout(progressTimeoutId);
          if (timeoutRef.current) callbackTimeoutManager.clearTimeout(timeoutRef.current);
          ConnectionFlowState.endUserFlow();
          oauthProtection.forceCompleteFlow();
          try { updateFlowProgress('wealthica-flow', 40, 'Connection successful! Finalizing…'); } catch {}
        },
        onError: (err) => {
          logger.error('Wealthica2CallbackHandler', 'CALLBACK ERROR - Processing failed', {
            error: err,
            userId: user.id,
            institution,
            provider,
            stateParam,
            timestamp: new Date().toISOString(),
            processingTime: Date.now() - startTime
          });
          clearTimeout(progressTimeoutId);
          if (timeoutRef.current) callbackTimeoutManager.clearTimeout(timeoutRef.current);
          ConnectionFlowState.endUserFlow();
          oauthProtection.forceCompleteFlow();
          toast.dismiss('wealthica-progress-warning');
          let errorMessage = 'Unknown error';
          if (typeof err === 'string') errorMessage = err as string;
          else if (err && typeof err === 'object' && 'message' in err) {
            const maybeMsg = (err as { message?: unknown }).message;
            errorMessage = typeof maybeMsg === 'string' ? maybeMsg : String(maybeMsg);
          }
          try { updateFlowToast('wealthica-flow', 'error', 'Connection failed', errorMessage); } catch {}
          setTimeout(() => {
            logger.info('Wealthica2CallbackHandler', 'Redirecting to investments after error');
            setIsShowingProcessing(false);
            navigate('/investments', { replace: true });
          }, 3000);
        }
      });
    } catch (error) {
      logger.error('Wealthica2CallbackHandler', 'CALLBACK EXCEPTION - Unexpected error during processing', {
        error,
        userId: user.id,
        institution,
        provider,
        stateParam,
        timestamp: new Date().toISOString(),
        errorStack: error instanceof Error ? error.stack : undefined
      });
      if (timeoutRef.current) callbackTimeoutManager.clearTimeout(timeoutRef.current);
      ConnectionFlowState.endUserFlow();
      const errorMessage = error instanceof Error ? error.message : String(error);
      try { updateFlowToast('wealthica-flow', 'error', 'Connection failed', errorMessage); } catch {}
      setTimeout(() => {
        logger.info('Wealthica2CallbackHandler', 'Redirecting to investments after exception');
        setIsShowingProcessing(false);
        navigate('/investments', { replace: true });
      }, 3000);
    }

    return () => {
      if (timeoutRef.current) callbackTimeoutManager.clearTimeout(timeoutRef.current);
    };
  }, [user?.id, institution, provider, stateParam]);

  const isRealCallback = isWealthica2Callback() && (institution || searchParams.get('code'));

  logger.debug('Wealthica2CallbackHandler', 'Render check', {
    isRealCallback,
    status: status,
    hasInstitution: !!institution,
    hasCode: !!searchParams.get('code')
  });

  const activeConn = multiStore.activeConnectionId ? multiStore.getConnection(multiStore.activeConnectionId) : undefined;
  const institutionLabel = activeConn?.connectionData?.institutionIdentifier
    ? (activeConn.connectionData.institutionIdentifier === 'demo' ? 'Wealthica Demo' : activeConn.connectionData.institutionIdentifier)
    : undefined;
  const { meta: instMeta } = useInstitutionMeta(activeConn?.connectionData?.institutionIdentifier);

  if (!isRealCallback) {
    logger.debug('Wealthica2CallbackHandler', 'Not a real callback - returning null');
  }

  const isProcessingCallback = isRealCallback && user && (isShowingProcessing || !processedRef.current);
  const shouldShowUI = !!opts?.enableUI && (status === 'connecting' || status === 'error' || isProcessingCallback || isShowingProcessing);
  const displayState = status === 'connecting' || isProcessingCallback ? 'connecting' : status === 'error' ? 'error' : 'success';

  return { shouldRender: isRealCallback, shouldShowUI, displayState, instMeta, institutionLabel, status, error } as const;
}
